import {
  ItemReplacementAction,
  useItemReplacementContext,
  useSearchItemsForSubstitution,
  useSearchItemsForSubstitutionInUPC,
} from '../../../../../src/itemDetails/hooks/itemReplacement';
import {Keyboard, StyleSheet} from 'react-native';
import React, {useCallback, useState} from 'react';
import {
  RecommendedItemCell,
  SearchType,
} from '../../../../../src/itemDetails/types/itemReplacement';
import {getClientType, getStoreName} from '@buncha/common/types/common';

import {Analytics} from '@buncha/config/analytics/analytics';
import {Box} from '../../../../../src/components/core/view';
import {CustomFlatList} from '../../../../../src/components/core/list';
import {Divider} from 'native-base';
import {EventType} from '@buncha/config/analytics/type';
import {H6Text} from '@buncha/components/core/text';
import If from '../../../../../src/components/conditional/If';
import LoaderComponents from '@buncha/components/composites/CustomLoader/LoaderComponents';
import RecommendedItems from './RecommendedItems';
import ReplacementItemCell from '../replacementItemCell';
import SearchInput from '../../../../../src/components/composites/Input/TextInput/SearchInput';
import {ShopperScreens} from '@buncha/home/navigation/types';
import {SpinnerComponent} from '@buncha/components/core/loader';
import {Touchable} from '@buncha/components/core/touchable';
import {navigation} from '@buncha/config/navigation';
import {useAppSelector} from '@buncha/config/redux/hooks';
import {useColorThemeType} from '../../../../../src/appStyles/hooks/theme';
import {useIsMeijerStore} from '@buncha/home/hooks/home';
import {Modules} from '@buncha/utils/types';
import {useGetLocale} from '@buncha/localization/hooks';

interface SearchResultsProps {
  catalogItemId: number;
  category: string;
  searchQuery: string;
  storeId: number;
  subCategory: string;
  isUPCMode: boolean;
  orderItemId?: number;
  orderId?: number;
  runId?: number;
  quantity?: number;
  onSearchFocus?: (isFocused: boolean) => void;
}
const SearchResults: React.FC<SearchResultsProps> = props => {
  const theme = useColorThemeType('main');
  const {state, dispatch} = useItemReplacementContext();
  const [searchQuery, setSearchQuery] = useState('');
  const {searchItemsForSubstitution, loading, moreItems} =
    useSearchItemsForSubstitution();
  const {selectedCatalogStoreForUPCRun} = useAppSelector(
    gState => gState.itemScanner,
  );
  const {body} =
    useGetLocale(Modules.itemDetails, ShopperScreens.ItemDetails) ?? {};

  const storeIndex = state.item?.order?.run?.store?.elasticSearchIndex;
  const storeId = state.item?.order?.run?.store?.id;
  const isMeijerStore = useIsMeijerStore(storeId);

  const {searchItemsForSubstitutioninUPC, loadingUPC, moreItemsUPC} =
    useSearchItemsForSubstitutionInUPC();
  const size = 10;
  const searchSource = 'Add Replacement';

  const onFocus = useCallback(() => {
    Analytics.eventWithProps('Searching for an item', {
      type: EventType.input,
      source: searchSource,
      client: getClientType(isMeijerStore),
      storeType: getStoreName(String(storeId)),
    });
    if (props?.onSearchFocus) props.onSearchFocus(true);
  }, [isMeijerStore, props, storeId]);

  const searchForAnItemEvent = useCallback(
    (keyword: string, offset: number) => {
      Analytics.eventWithProps('Search for an item', {
        searchItem: keyword,
        from: offset,
        size: size,
        source: searchSource,
        storeId: props.storeId,
        catalogItemId: props.catalogItemId,
        client: getClientType(isMeijerStore),
        storeType: getStoreName(String(storeId)),
      });
    },
    [isMeijerStore, props.catalogItemId, props.storeId, storeId],
  );

  const handleSearchQueryChange = useCallback(
    async (text: string) => {
      if (props?.onSearchFocus) props.onSearchFocus(true);
      setSearchQuery(text);
      if (!text && dispatch)
        dispatch(ItemReplacementAction.setSearchResults([]));
      if (text) {
        searchForAnItemEvent(text, 0);
        if (props?.isUPCMode)
          await searchItemsForSubstitutioninUPC({
            esIndex: selectedCatalogStoreForUPCRun?.esIndex ?? '',
            from: 0,
            size: 10,
            searchQuery: text,
            catalogItemId: props?.catalogItemId,
            runId: props?.runId,
          });
        else
          await searchItemsForSubstitution({
            storeId: props?.storeId || 0,
            from: 0,
            size: 10,
            searchQuery: text,
            catalogItemId: props?.catalogItemId,
            searchType: SearchType.substitutionSearch,
            runId: props?.runId,
          });
      }
    },
    [
      dispatch,
      props,
      searchForAnItemEvent,
      searchItemsForSubstitution,
      searchItemsForSubstitutioninUPC,
      selectedCatalogStoreForUPCRun?.esIndex,
    ],
  );

  const handleScroll = useCallback(
    async (_: any, offset: number) => {
      searchForAnItemEvent(searchQuery, offset);
      if (props?.isUPCMode)
        await searchItemsForSubstitutioninUPC({
          esIndex: selectedCatalogStoreForUPCRun?.esIndex ?? '',
          from: offset,
          size: 10,
          searchQuery: searchQuery,
          catalogItemId: props?.catalogItemId,
        });
      else
        await searchItemsForSubstitution({
          storeId: props?.storeId || 0,
          from: offset,
          size: 10,
          searchQuery: searchQuery,
          catalogItemId: props?.catalogItemId,
          searchType: SearchType.substitutionSearch,
        });
    },
    [
      props?.catalogItemId,
      props?.isUPCMode,
      props?.storeId,
      searchForAnItemEvent,
      searchItemsForSubstitution,
      searchItemsForSubstitutioninUPC,
      searchQuery,
      selectedCatalogStoreForUPCRun?.esIndex,
    ],
  );

  const separator = useCallback(() => <Divider />, []);

  const onScroll = useCallback(() => {
    Keyboard.dismiss();
  }, []);

  const navigateToScanningScreen = useCallback(() => {
    if (storeIndex && props.orderId && props.runId)
      navigation.navigate(ShopperScreens.ScannedAddItemPage, {
        storeIndex: storeIndex,
        runId: props.runId,
        orderId: props.orderId,
        storeId: props.storeId,
        returnPage: ShopperScreens.BatchDetailsPage,
        orderItemId: state.item?.id,
        source: 'direct scan',
      });
  }, [props.orderId, props.runId, props.storeId, state.item?.id, storeIndex]);

  const renderItem = useCallback(
    (subItem: {item: RecommendedItemCell}) => {
      return (
        <ReplacementItemCell
          itemInfo={subItem?.item}
          isUPCMode={props?.isUPCMode ?? false}
          orderItemId={props?.orderItemId}
          orderId={props?.orderId}
          runId={props?.runId}
          quantity={props?.quantity}
          isB2BStore={isMeijerStore}
        />
      );
    },
    [
      isMeijerStore,
      props?.isUPCMode,
      props?.orderId,
      props?.orderItemId,
      props?.quantity,
      props?.runId,
    ],
  );
  return (
    <Box style={styles.parentBox}>
      <SearchInput
        defaultValue={searchQuery}
        containerStyle={[
          styles.search,
          {
            backgroundColor: theme?.background?.white?.[100],
          },
        ]}
        debounce={true}
        placeholderTextColor={theme?.text?.blue?.[300]}
        placeholder={body?.searchItem}
        textValue={searchQuery}
        clearInput={true}
        colorThemeType="default"
        onChangeText={handleSearchQueryChange}
        style={{color: theme?.text?.blue?.[300]}}
        onFocus={onFocus}
        onBlur={() => {
          if (props?.onSearchFocus) props.onSearchFocus(false);
        }}
      />

      <If condition={isMeijerStore}>
        <Touchable style={styles.outerText} onPress={navigateToScanningScreen}>
          <H6Text>
            {body?.scanBarcode?.[0]}
            <H6Text
              style={[
                styles.innerText,
                {
                  color: theme?.text?.blue?.[100],
                },
              ]}>
              {body?.scanBarcode?.[1]}
            </H6Text>
          </H6Text>
        </Touchable>
      </If>

      <Divider style={styles.divider} />
      <If
        condition={
          state?.searchResults.length ||
          ((loading || loadingUPC) && searchQuery)
        }>
        {state.searchResults?.length > 0 && (
          <CustomFlatList
            showsVerticalScrollIndicator={false}
            data={state.searchResults}
            renderItem={renderItem}
            ItemSeparatorComponent={separator}
            loadMore={props?.isUPCMode ? moreItemsUPC : moreItems}
            loading={loading || loadingUPC}
            userOffset={10}
            initialOffset={10}
            onEndReachedThreshold={0.5}
            numOfTimesToRender={1}
            loadingSkeleton={<SpinnerComponent style={{marginTop: 20}} />}
            onEndReachedAtt={handleScroll}
            onScroll={onScroll}
          />
        )}
      </If>
      <If
        condition={
          (loading || loadingUPC) && state?.searchResults.length === 0
        }>
        <LoaderComponents showBottomText={false} />
      </If>
      <If
        condition={
          !searchQuery &&
          (!loading || !loadingUPC) &&
          !state.searchResults.length
        }>
        <RecommendedItems
          catalogItemId={props?.catalogItemId}
          category={props?.category}
          searchQuery={props?.searchQuery}
          storeId={props?.storeId}
          subCategory={props?.subCategory}
          isUPCMode={props?.isUPCMode ?? false}
          orderItemId={props?.orderItemId}
          orderId={props?.orderId}
          runId={props?.runId}
          quantity={props?.quantity}
        />
      </If>
    </Box>
  );
};
export default React.memo(SearchResults);
const styles = StyleSheet.create({
  search: {
    height: 55,
    borderRadius: 50,
    marginBottom: 5,
  },
  parentBox: {
    flex: 1,
  },
  scanButton: {
    width: '100%',
  },
  bottomButtonContainer: {
    paddingBottom: 0,
    paddingTop: 0,
  },
  divider: {
    marginTop: 8,
  },
  outerText: {marginTop: 5},
  innerText: {
    fontWeight: 'bold',
    marginLeft: 5,
    marginTop: 5,
  },
});
